/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IFooterViewProps } from '@msdyn365-commerce-modules/footer';
import {
    getPayloadObject,
    getTelemetryObject,
    IPayLoad,
    ITelemetryContent,
    Module,
    Node,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { navLinkKeywordRemoval } from '../../../Utilities/plp-url-utils';
import { isExternalLink } from '../../../Utilities/isExternalLink';

/**
 *
 * FooterItemView component
 * @extends {React.PureComponent<IFooterViewProps>}
 */
export class FooterItemView extends React.PureComponent<IFooterViewProps> {
    private telemetryContent: ITelemetryContent;
    private payLoad: IPayLoad;
    private attributes: Msdyn365.IDictionary<string> | undefined;
    constructor(props: IFooterViewProps) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        // Construct telemetry attribute to render
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }
    public render(): JSX.Element | null {
        const { FooterItem } = this.props;
        return <Module {...FooterItem}>{this._renderElement(this.props)}</Module>;
    }

    public handleLinkTextChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.link!.linkText = event.target.value);

    public handleLinkClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const { link } = this.props.config;
        const linkTexT = link && link.linkText ? link.linkText : '';
        onTelemetryClick(this.telemetryContent, this.payLoad, linkTexT);
        // @ts-ignore
        if (utag) {
            event.preventDefault();
            const isExternal = isExternalLink(link?.linkUrl.destinationUrl);
            const eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
            const eventCategory = isExternal ? 'outbound links' : 'internal links';
            const internalDestinationUrl =
                link?.linkUrl.destinationUrl === '/' ? '/' : link?.linkUrl.destinationUrl.split(location.host)[1];
            const internalLocalUrl =
                Msdyn365.msdyn365Commerce.isBrowser && window.location.href === '/'
                    ? '/'
                    : window.location.href.split(location.host)[1].split('?')[0];
            const eventAction = isExternal ? `to: ${link?.linkUrl.destinationUrl}` : `to: ${internalDestinationUrl}`;
            const eventLabel = isExternal ? `from footer: ${window.location.href}` : `from footer: ${internalLocalUrl}`;
            const isOpenInNewTab = link?.openInNewTab;
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: eventName,
                event_category: eventCategory,
                event_action: eventAction,
                event_label: eventLabel,
                event_noninteraction: 'false'
            });
            !isOpenInNewTab
                ? (window.location.href = navLinkKeywordRemoval(link?.linkUrl.destinationUrl!, this.props.context.request))
                : window.open(navLinkKeywordRemoval(link?.linkUrl.destinationUrl!, this.props.context.request));
        }
    };

    private _renderElement(props: IFooterViewProps): React.ReactNode | null {
        const { heading, HeadingContainer, Link, linkText, image, text, context, config } = props;
        if (heading) {
            return <Node {...HeadingContainer}> {heading} </Node>;
        } else if (Link && image) {
            return (
                <Node
                    {...Link}
                    // tslint:disable-next-line: jsx-no-lambda react-this-binding-issue
                    onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                        // @ts-ignore
                        if (utag) {
                            event.preventDefault();
                            const isOpenInNewTab = this.props.config.link?.openInNewTab;
                            // @ts-ignore
                            // tslint:disable-next-line: object-literal-key-quotes max-line-length
                            utag.link({
                                event: 'outbound-social-link-click',
                                event_category: 'outbound links',
                                event_action: 'social link',
                                event_label: `to: ${navLinkKeywordRemoval(config.link?.linkUrl.destinationUrl!, context.request)}`,
                                event_noninteraction: 'false'
                            });
                            !isOpenInNewTab
                                ? (window.location.href = navLinkKeywordRemoval(config.link?.linkUrl.destinationUrl!, context.request))
                                : window.open(navLinkKeywordRemoval(config.link?.linkUrl.destinationUrl!, context.request));
                        }
                    }}
                >
                    {linkText}
                    {image}
                </Node>
            );
        } else if (Link) {
            const { link } = this.props.config;
            if (link) {
                const linkTexT = link.linkText ? link.linkText : '';
                // TODO: Update the inner tag in the anchor link to span
                const editableLink: Msdyn365.ILinksData = {
                    ariaLabel: link.ariaLabel,
                    className: 'ms-footer__link',
                    linkText: linkTexT,
                    linkUrl: link.linkUrl.destinationUrl,
                    openInNewTab: link.openInNewTab,
                    role: 'button',
                    additionalProperties: this.attributes!,
                    onClick: this.handleLinkClick
                };
                return (
                    <Msdyn365.Link
                        link={editableLink}
                        editProps={{ onTextChange: this.handleLinkTextChange, requestContext: this.props.context.request }}
                    />
                );
            }
        }
        return (
            <>
                {text}
                {image}
            </>
        );
    }
}

export default FooterItemView;
